import { DriverStatus, RideStatus } from '@taxi/shared';

/**
 * Présentation visuelle des statuts.
 *
 * Source unique pour les trois interfaces : couleur, pictogramme et clé de
 * traduction d'un même état. Sans elle, chaque application refaisait sa propre
 * table de correspondance, et un statut finissait bleu ici, violet là — le
 * client, le chauffeur et l'agent d'exploitation ne parlaient plus du même
 * incident.
 *
 * Les couleurs sont des noms du thème (`searching`, `enroute`, `onboard`…),
 * définis une fois dans `MonacoTheme`.
 */
export interface StatusPresentation {
	color: string;
	icon: string;
	/**
	 * Clé i18n **courte**, pour une pastille.
	 *
	 * Distincte du titre de l'écran, qui est une phrase : afficher la même chose
	 * dans la pastille et dans le titre juste au-dessus revient à l'écrire deux
	 * fois.
	 */
	labelKey: string;
	/** Libellé court en français, pour le back-office monolingue. */
	label: string;
}

export const RIDE_STATUS_PRESENTATION: Record<RideStatus, StatusPresentation> = {
	[RideStatus.DRAFT]: {
		color: 'offline',
		icon: 'mdi-pencil-outline',
		labelKey: 'ride.status.draft',
		label: 'Brouillon',
	},
	[RideStatus.SCHEDULED]: {
		color: 'accent',
		icon: 'mdi-calendar-clock',
		labelKey: 'ride.status.scheduled',
		label: 'Réservée',
	},
	[RideStatus.SEARCHING]: {
		color: 'searching',
		icon: 'mdi-radar',
		labelKey: 'ride.status.searching',
		label: 'Recherche',
	},
	[RideStatus.ACCEPTED]: {
		color: 'enroute',
		icon: 'mdi-taxi',
		labelKey: 'ride.status.accepted',
		label: 'Acceptée',
	},
	[RideStatus.DRIVER_ARRIVED]: {
		color: 'available',
		icon: 'mdi-map-marker-check',
		labelKey: 'ride.status.arrived',
		label: 'Sur place',
	},
	[RideStatus.IN_PROGRESS]: {
		color: 'onboard',
		icon: 'mdi-navigation',
		labelKey: 'ride.status.inProgress',
		label: 'En cours',
	},
	[RideStatus.COMPLETED]: {
		color: 'success',
		icon: 'mdi-check-circle',
		labelKey: 'ride.status.completed',
		label: 'Terminée',
	},
	[RideStatus.CANCELLED]: {
		color: 'error',
		icon: 'mdi-close-circle',
		labelKey: 'ride.status.cancelled',
		label: 'Annulée',
	},
	[RideStatus.NO_DRIVER]: {
		color: 'warning',
		icon: 'mdi-car-off',
		labelKey: 'ride.status.noDriver',
		label: 'Sans chauffeur',
	},
};

export const DRIVER_STATUS_PRESENTATION: Record<DriverStatus, StatusPresentation> = {
	[DriverStatus.OFFLINE]: {
		color: 'offline',
		icon: 'mdi-sleep',
		labelKey: 'driver.status.OFFLINE',
		label: 'Hors service',
	},
	[DriverStatus.AVAILABLE]: {
		color: 'available',
		icon: 'mdi-check-circle',
		labelKey: 'driver.status.AVAILABLE',
		label: 'Disponible',
	},
	[DriverStatus.PAUSED]: {
		color: 'warning',
		icon: 'mdi-pause-circle',
		labelKey: 'driver.status.PAUSED',
		label: 'En pause',
	},
	[DriverStatus.EN_ROUTE]: {
		color: 'enroute',
		icon: 'mdi-taxi',
		labelKey: 'driver.status.EN_ROUTE',
		label: 'En course',
	},
	[DriverStatus.ON_BOARD]: {
		color: 'onboard',
		icon: 'mdi-account-check',
		labelKey: 'driver.status.ON_BOARD',
		label: 'En charge',
	},
};

export const rideStatusPresentation = (status: RideStatus): StatusPresentation =>
	RIDE_STATUS_PRESENTATION[status] ?? RIDE_STATUS_PRESENTATION[RideStatus.DRAFT];

export const driverStatusPresentation = (status: DriverStatus): StatusPresentation =>
	DRIVER_STATUS_PRESENTATION[status] ?? DRIVER_STATUS_PRESENTATION[DriverStatus.OFFLINE];

/**
 * Couleur d'un statut chauffeur en valeur hexadécimale.
 *
 * Les marqueurs de carte sont dessinés hors du système de thème Vuetify : ils ne
 * peuvent pas résoudre `rgb(var(--v-theme-…))` et ont besoin d'une valeur brute.
 */
export const DRIVER_STATUS_HEX: Record<DriverStatus, string> = {
	[DriverStatus.OFFLINE]: '#78909C',
	[DriverStatus.AVAILABLE]: '#2E7D32',
	[DriverStatus.PAUSED]: '#ED6C02',
	[DriverStatus.EN_ROUTE]: '#2A6EBB',
	[DriverStatus.ON_BOARD]: '#6A1B9A',
};
